<template>
  <div class="modal-overlay">
    <div class="modal-card">
      <!-- HEADER -->
      <div class="modal-header">
        <h3>Edit Patient Details</h3>
        <button class="close-btn" @click="close">&times;</button>
      </div>
      <div class="modal-body" v-if="loading">
        <loader />
      </div>

      <!-- BODY -->
      <div v-if="!loading" class="modal-body">
        <!-- Tabs Navigation -->
        <div class="tabs">
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'patient' }"
            @click="activeTab = 'patient'"
          >
            Patient Details
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'nextofkin' }"
            @click="activeTab = 'nextofkin'"
          >
            Next of Kin
          </button>
        </div>

        <!-- Patient Tab -->
        <div v-if="activeTab === 'patient'" class="tab-content">
          <form class="form-grid" @submit.prevent="savePatient">
            <div class="form-group full">
              <label>Full Name</label>
              <input type="text" v-model="form.name" required />
            </div>

            <div class="form-group">
              <label>Date of Birth</label>
              <input type="date" v-model="form.dob" required />
            </div>

            <div class="form-group">
              <label>Gender</label>
              <select v-model="form.gender" required>
                <option value="">Select</option>
                <option value="1">Male</option>
                <option value="2">Female</option>
                <option value="3">Other</option>
              </select>
            </div>

            <div class="form-group">
              <label>ID / Passport No.</label>
              <input type="text" v-model="form.id_number" />
            </div>

            <div class="form-group">
              <label>Phone Number</label>
              <input type="tel" v-model="form.phone" />
            </div>

            <div class="form-group">
              <label>Email Address</label>
              <input type="email" v-model="form.email" />
            </div>

            <div class="form-group full">
              <label>Location</label>
              <textarea rows="2" v-model="form.location"></textarea>
            </div>
          </form>
        </div>

        <!-- Next of Kin Tab -->
        <div v-if="activeTab === 'nextofkin'" class="tab-content">
          <div class="nextofkin-header">
            <h4>Next of Kin Information</h4>
            <button
              v-if="hasNextOfKin"
              class="btn danger small"
              @click="confirmDeleteNextOfKin"
            >
              Remove Next of Kin
            </button>
          </div>

          <form class="form-grid" @submit.prevent="saveNextOfKin">
            <div class="form-group full">
              <label>Full Name *</label>
              <input
                type="text"
                v-model="nextOfKinForm.name"
                :required="hasNextOfKin"
                placeholder="Enter full name"
              />
            </div>

            <div class="form-group">
              <label>Relationship *</label>
              <select
                v-model="nextOfKinForm.relationship"
                :required="hasNextOfKin"
              >
                <option value="">Select Relationship</option>
                <option value="spouse">Spouse</option>
                <option value="parent">Parent</option>
                <option value="child">Child</option>
                <option value="sibling">Sibling</option>
                <option value="relative">Relative</option>
                <option value="friend">Friend</option>
                <option value="guardian">Guardian</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div class="form-group">
              <label>Gender</label>
              <select v-model="nextOfKinForm.gender">
                <option value="">Select</option>
                <option value="1">Male</option>
                <option value="2">Female</option>
                <option value="3">Other</option>
              </select>
            </div>

            <div class="form-group">
              <label>Phone Number *</label>
              <input
                type="tel"
                v-model="nextOfKinForm.phone"
                :required="hasNextOfKin"
                placeholder="Emergency contact number"
              />
            </div>

            <div class="form-group">
              <label>Email Address</label>
              <input
                type="email"
                v-model="nextOfKinForm.email"
                placeholder="Optional email"
              />
            </div>

            <div class="form-group">
              <label>ID / Passport No.</label>
              <input
                type="text"
                v-model="nextOfKinForm.id_number"
                placeholder="Optional ID number"
              />
            </div>

            <div class="form-group full">
              <label>Residential Address</label>
              <textarea
                rows="2"
                v-model="nextOfKinForm.address"
                placeholder="Full residential address"
              ></textarea>
            </div>

            <!-- <div class="form-group full">
              <div class="checkbox-group">
                <input
                  type="checkbox"
                  id="emergencyContact"
                  v-model="nextOfKinForm.is_emergency_contact"
                />
                <label for="emergencyContact" class="checkbox-label">
                  Designate as Primary Emergency Contact
                </label>
              </div>
            </div> -->

            <!-- <div class="form-group full">
              <label>Additional Information</label>
              <textarea
                rows="3"
                v-model="nextOfKinForm.additional_info"
                placeholder="Any additional notes or special instructions"
              ></textarea>
            </div> -->
          </form>
        </div>

        <div class="modal-footer">
          <button class="btn secondary" @click="$emit('close')">Cancel</button>
          <button
            v-if="activeTab === 'patient'"
            class="btn primary"
            @click="savePatient"
            :disabled="saving"
          >
            {{ saving ? "Saving..." : "Save Patient Details" }}
          </button>
          <button
            v-if="activeTab === 'nextofkin'"
            class="btn primary"
            @click="saveNextOfKin"
            :disabled="saving"
          >
            {{
              saving
                ? "Saving..."
                : hasNextOfKin
                ? "Update Next of Kin"
                : "Add Next of Kin"
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="modal-overlay confirm-overlay">
      <div class="confirm-modal">
        <div class="confirm-header">
          <h4>Remove Next of Kin</h4>
        </div>
        <div class="confirm-body">
          <p>
            Are you sure you want to remove
            <strong>{{ nextOfKinForm.name }}</strong> as next of kin?
          </p>
          <p class="warning-text">This action cannot be undone.</p>
        </div>
        <div class="confirm-footer">
          <button class="btn secondary" @click="showDeleteConfirm = false">
            Cancel
          </button>
          <button class="btn danger" @click="deleteNextOfKin">
            Yes, Remove
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import loader from "../../special_clinics/dialysis/loader.vue";
import { generalApiRequest } from "../../../api/apiIndex";

export default {
  name: "EditPatientModal",

  props: {
    patient_id: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      loading: true,
      saving: false,
      activeTab: "patient",
      showDeleteConfirm: false,
      form: {
        user_id: "",
        name: "",
        dob: "",
        gender: "",
        id_number: "",
        phone: "",
        email: "",
        location: "",
      },
      nextOfKinForm: {
        patient_id: "",
        name: "",
        relationship: "",
        gender: "",
        phone: "",
        email: "",
        id_number: "",
        address: "",
        is_emergency_contact: true,
        additional_info: "",
      },
      originalNextOfKin: null,
    };
  },

  components: {
    loader,
  },

  computed: {
    hasNextOfKin() {
      return (
        this.nextOfKinForm.name &&
        this.nextOfKinForm.relationship &&
        this.nextOfKinForm.phone
      );
    },
  },

  methods: {
    close() {
      this.$emit("close");
    },

    async savePatient() {
      this.saving = true;
      try {
        const url = "update_user";
        const body = this.form;
        await generalApiRequest(url, "POST", body, 1);
        this.$emit("saved");
        this.close();
      } catch (error) {
        console.error("Error saving patient:", error);
        alert("Failed to save patient details");
      } finally {
        this.saving = false;
      }
    },

    async saveNextOfKin() {
      if (!this.hasNextOfKin) {
        alert("Please fill in at least Name, Relationship, and Phone Number");
        return;
      }

      this.saving = true;
      try {
        const url = "update_next_of_kin";
        // this.originalNextOfKin
        //   ? "update_next_of_kin"
        //   : "add_next_of_kin";
        const body = {
          ...this.nextOfKinForm,
          patient_id: this.patient_id,
        };

        await generalApiRequest(url, "POST", body, 1);
        this.$emit("saved");
        alert("Next of Kin details saved successfully!");
        this.close();
      } catch (error) {
        console.error("Error saving next of kin:", error);
        alert("Failed to save next of kin details");
      } finally {
        this.saving = false;
      }
    },

    async deleteNextOfKin() {
      try {
        const url = "delete_next_of_kin";
        const body = {
          patient_id: this.patient_id,
        };

        await generalApiRequest(url, "POST", body, 1);
        this.nextOfKinForm = this.getEmptyNextOfKinForm();
        this.originalNextOfKin = null;
        this.showDeleteConfirm = false;
        alert("Next of Kin removed successfully!");
        this.$emit("saved");
      } catch (error) {
        console.error("Error deleting next of kin:", error);
        alert("Failed to remove next of kin");
      }
    },

    confirmDeleteNextOfKin() {
      this.showDeleteConfirm = true;
    },

    getEmptyNextOfKinForm() {
      return {
        patient_id: this.patient_id,
        name: "",
        relationship: "",
        gender: "",
        phone: "",
        email: "",
        id_number: "",
        address: "",
        is_emergency_contact: true,
        additional_info: "",
      };
    },

    async get_user() {
      this.loading = true;
      try {
        const url = "getUserById";
        const body = { user_id: this.patient_id };
        const res = await generalApiRequest(url, "POST", body, 1);

        const rs = res.user;
        const nextOfKin = res.next_of_kin_details || {};

        // Populate patient form
        this.form = {
          user_id: this.patient_id,
          name: rs.name,
          dob: rs.dob,
          gender: rs.gender,
          id_number: rs.id_number,
          phone: rs.phone,
          email: rs.email,
          location: rs.location,
        };

        // Populate next of kin form
        if (nextOfKin && Object.keys(nextOfKin).length > 0) {
          this.nextOfKinForm = {
            patient_id: this.patient_id,
            name: nextOfKin.name || "",
            relationship: rs.nok_relationship || "",
            gender: nextOfKin.gender || "",
            phone: nextOfKin.phone || "",
            email: nextOfKin.email || "",
            id_number: nextOfKin.id_number || "",
            address: nextOfKin.location || "",
            is_emergency_contact: nextOfKin.is_emergency_contact !== false,
            additional_info: nextOfKin.additional_info || "",
          };
          this.originalNextOfKin = { ...nextOfKin };
        } else {
          this.nextOfKinForm = this.getEmptyNextOfKinForm();
        }
      } catch (error) {
        console.error("Error fetching user:", error);
        alert("Failed to load patient details");
      } finally {
        this.loading = false;
      }
    },
  },

  mounted() {
    this.get_user();
  },
};
</script>

<style scoped>
/* OVERLAY */
.modal-overlay {
  position: fixed;
  inset: 0;

  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

/* CARD */
.modal-card {
  /* width: 750px; */
  width: 70vw;
  max-width: 95%;
  max-height: 90vh;
  background: #fff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}

/* HEADER */
.modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.2rem;
}

/* CLOSE */
.close-btn {
  background: #ef4444;
  border: none;
  color: #fff;
  font-size: 1.4rem;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
}

/* BODY */
.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

/* TABS */
.tabs {
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 1.5rem;
}

.tab-btn {
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.95rem;
  color: #6b7280;
  position: relative;
  transition: color 0.2s;
}

.tab-btn:hover {
  color: #374151;
}

.tab-btn.active {
  color: #4f46e5;
  font-weight: 600;
}

.tab-btn.active::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 2px;
  background: #4f46e5;
}

/* TAB CONTENT */
.tab-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* NEXT OF KIN HEADER */
.nextofkin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.nextofkin-header h4 {
  margin: 0;
  color: #374151;
}

/* GRID FORM */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem 1.2rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full {
  grid-column: span 2;
}

label {
  font-size: 0.85rem;
  color: #374151;
  margin-bottom: 4px;
  font-weight: 500;
}

input,
select,
textarea {
  padding: 0.55rem 0.65rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 0.9rem;
  transition: border-color 0.2s;
}

input:focus,
select:focus,
textarea:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
}

/* CHECKBOX GROUP */
.checkbox-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.checkbox-group input[type="checkbox"] {
  width: 18px;
  height: 18px;
  margin: 0;
}

.checkbox-label {
  margin: 0;
  cursor: pointer;
  font-size: 0.9rem;
  color: #374151;
}

/* FOOTER */
.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

/* BUTTONS */
.btn {
  padding: 0.55rem 1.2rem;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s;
  min-width: 120px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn.primary {
  background: #4f46e5;
  color: #fff;
}

.btn.primary:hover:not(:disabled) {
  background: #4338ca;
}

.btn.secondary {
  background: #e5e7eb;
  color: #374151;
}

.btn.secondary:hover:not(:disabled) {
  background: #d1d5db;
}

.btn.danger {
  background: #ef4444;
  color: #fff;
}

.btn.danger:hover:not(:disabled) {
  background: #dc2626;
}

.btn.danger.small {
  padding: 0.35rem 0.75rem;
  font-size: 0.8rem;
  min-width: auto;
}

/* DELETE CONFIRM MODAL */
.confirm-overlay {
  background: rgba(0, 0, 0, 0.7);
}

.confirm-modal {
  width: 400px;
  max-width: 90%;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.confirm-header {
  padding: 1rem 1.5rem;
  background: #fef2f2;
  border-bottom: 1px solid #fecaca;
}

.confirm-header h4 {
  margin: 0;
  color: #dc2626;
}

.confirm-body {
  padding: 1.5rem;
}

.confirm-body p {
  margin: 0 0 0.75rem 0;
  color: #374151;
}

.warning-text {
  color: #dc2626 !important;
  font-weight: 500;
}

.confirm-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

/* RESPONSIVE DESIGN */
@media (max-width: 768px) {
  .modal-card {
    width: 95%;
    margin: 10px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full {
    grid-column: span 1;
  }

  .tabs {
    flex-direction: column;
  }

  .tab-btn {
    width: 100%;
    text-align: left;
  }
}
</style>
